import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  IImageManagementTaskDTO,
  ImageManagementResultDTO,
  IImageManagementResultDTO,
  ImageManagementTaskDTO,
  SearchConstraintDTO,
} from 'business/models/Models';
import { imageTaskNameKey } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import { cloneDeep } from 'lodash';
import {
  getImageManagementTasks,
  saveImageManagementTasks,
  startImageManagementTasks,
} from 'redux/thunks/imageManagementTask';
import { showSnackBar } from 'utils/Global';

export type ImageManagementTasksState = {
  imageManagementTaskList: IImageManagementTaskDTO[];
  imageManagementTaskOriginalList: IImageManagementTaskDTO[];
  isDirty: boolean;
  selectedImageTaskId: number;
  selectedImageActionId: number;
  selectedImageId: number;
  checkedExplorerNodes: number[];
  deletedActionIds: number[];
  imageManagementTaskResultList: ImageManagementResultDTO[];
  isProcessedChecked: boolean;
  isPendingChecked: boolean;
  isErrorChecked: boolean;
  resultCount: number;
  isRunning: boolean;
  isPreview: boolean;
  isError: boolean;
  isResultEmpty: boolean;
  isImageManagementTaskListLoading: boolean;
  displayResults: IImageManagementResultDTO[]; // filtered imageManagementTaskResultList for display
  progress: number;
};

const initialState = {
  imageManagementTaskList: [],
  imageManagementTaskOriginalList: [],
  isDirty: false,
  selectedImageTaskId: 0,
  selectedImageActionId: 0,
  checkedExplorerNodes: [],
  deletedActionIds: [],
  isProcessedChecked: true,
  isPendingChecked: true,
  isErrorChecked: true,
  resultCount: 0,
  isRunning: false,
  isPreview: false,
  isError: false,
  isImageManagementTaskListLoading: false,
  displayResults: [],
  progress: 0,
} as ImageManagementTasksState;

const imageManagementTaskReducer = createSlice({
  name: 'ImageManagementTasks',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getImageManagementTasks.pending, (state, action) => {
      state.isImageManagementTaskListLoading = true;
    });
    builder.addCase(getImageManagementTasks.fulfilled, (state, action) => {
      state.imageManagementTaskList = action.payload;
      state.imageManagementTaskOriginalList = cloneDeep(action.payload);
      state.isImageManagementTaskListLoading = false;
    });
    builder.addCase(getImageManagementTasks.rejected, (state, action) => {
      state.isImageManagementTaskListLoading = false;
    });
    builder.addCase(saveImageManagementTasks.pending, (state, action) => {});
    builder.addCase(saveImageManagementTasks.fulfilled, (state, action) => {
      state.imageManagementTaskList = action.payload;
      state.imageManagementTaskOriginalList = cloneDeep([...action.payload]);
      state.isDirty = false;
      state.deletedActionIds = [];
      showSnackBar('Image Management Tasks saved.');
    });
    builder.addCase(startImageManagementTasks.pending, (state, action) => {});
    builder.addCase(startImageManagementTasks.fulfilled, (state, action) => {
      if (action.payload?.length > 0) {
        state.imageManagementTaskResultList = action.payload;
        state.isResultEmpty = false;
      } else {
        state.isResultEmpty = true;
      }
    });
    builder.addCase(startImageManagementTasks.rejected, (state, action) => {
      state.imageManagementTaskResultList = [];
      state.isResultEmpty = true;
      state.isError = true;
    });
  },
  reducers: {
    setImageManagementTaskList: (state, action) => {
      state.imageManagementTaskList = action.payload;
    },
    setImageManagementTaskOriginalList: (state, action) => {
      state.imageManagementTaskOriginalList = action.payload;
    },
    setImageManagementTaskResultList: (state, action) => {
      state.imageManagementTaskResultList = action.payload;
    },
    updateIsImageManagementTaskResultEmpty: (state, action) => {
      state.isResultEmpty = action.payload;
    },
    updateSelectedImageTaskResults: (
      state,
      action: PayloadAction<{ key: string; value; selectedTaskId: number }>,
    ) => {
      if (!state.imageManagementTaskResultList || !action.payload.selectedTaskId) {
        return;
      }
      const selectedTask = state.imageManagementTaskList.find(
        (t) => t.id === action.payload.selectedTaskId,
      );
      const updatedTaskResults = [...state.imageManagementTaskResultList];
      const imageManagementActionList = selectedTask.imageManagementActionList ?? [];
      if (
        imageManagementActionList.length > 0 &&
        imageManagementActionList?.some((action) =>
          updatedTaskResults?.find((q) => q.actionId == action.id),
        )
      ) {
        const index = updatedTaskResults.findIndex(
          (result) =>
            result?.actionId === action.payload.value.actionId &&
            result?.imageId === action.payload.value.imageId,
        );
        if (index >= 0) {
          updatedTaskResults[index][action.payload.key] = action.payload.value.status;
        }

        state.imageManagementTaskResultList = updatedTaskResults;
      }
    },
    updateSelectedImageTaskResultsBatch: (
      state,
      action: PayloadAction<Array<{ key: string; value: any; selectedTaskId: number }>>,
    ) => {
      if (!state.imageManagementTaskResultList || action.payload.length === 0) {
        return;
      }

      for (const payload of action.payload) {
        if (!payload.selectedTaskId) {
          continue;
        }

        const selectedTask = state.imageManagementTaskList.find(
          (t) => t.id === payload.selectedTaskId,
        );
        if (!selectedTask) {
          continue;
        }

        const updatedTaskResults = [...state.imageManagementTaskResultList];
        const imageManagementActionList = selectedTask.imageManagementActionList ?? [];

        if (
          imageManagementActionList.length > 0 &&
          imageManagementActionList.some((action) =>
            updatedTaskResults.find((q) => q.actionId == action.id),
          )
        ) {
          const index = updatedTaskResults.findIndex(
            (result) =>
              result?.actionId === payload.value.actionId &&
              result?.imageId === payload.value.imageId,
          );

          if (index >= 0) {
            updatedTaskResults[index][payload.key] = payload.value.status;
          }

          state.imageManagementTaskResultList = updatedTaskResults;
        }
      }
    },
    updateImageActionsByTaskID: (state, action) => {
      const { taskId, actions } = action.payload;
      state.isDirty = true;
      const tasksList: ImageManagementTaskDTO[] = state.imageManagementTaskList.map(
        (imageTask: ImageManagementTaskDTO) => {
          if (imageTask.id === taskId) {
            imageTask.imageManagementActionList = actions;
          }
          return imageTask as ImageManagementTaskDTO;
        },
      );
      state.imageManagementTaskList = tasksList;
    },
    updateSelectedImageTaskActions: (
      state,
      action: PayloadAction<{ key: string; value }>,
    ) => {
      const actionsList =
        state.imageManagementTaskList?.find(
          (task) => task.id === state.selectedImageTaskId,
        )?.imageManagementActionList || [];
      for (const act of actionsList) {
        if (act?.id === state.selectedImageActionId) {
          act[action.payload.key] = action.payload.value;
          act.isDirty = true;
        }
      }
      imageManagementTaskReducer.caseReducers.updateImageActionsByTaskID(state, {
        type: 'updateImageActionsByTaskID',
        payload: { taskId: state.selectedImageTaskId, actions: [...actionsList] },
      });
    },
    setImageManagementTasksIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },
    setSelectedImageTaskId: (state, action) => {
      state.selectedImageTaskId = action.payload;
      state.checkedExplorerNodes = state?.imageManagementTaskList.find(
        (q) => q.id === state?.selectedImageTaskId,
      )?.explorerNodeIds;
    },
    setSelectedImageActionId: (state, action) => {
      state.selectedImageActionId = action.payload;
    },
    setCheckedExplorerNodes: (state, action) => {
      state.checkedExplorerNodes = action.payload;
      state.isDirty = true;
    },
    setIsProcessedChecked: (state, action: PayloadAction<boolean>) => {
      state.isProcessedChecked = action.payload;
    },
    setIsPendingChecked: (state, action: PayloadAction<boolean>) => {
      state.isPendingChecked = action.payload;
    },
    setIsErrorChecked: (state, action: PayloadAction<boolean>) => {
      state.isErrorChecked = action.payload;
    },
    onAddNewTaskAction: (state, action) => {
      const actionsList =
        state.imageManagementTaskList?.find(
          (task) => task.id === state.selectedImageTaskId,
        )?.imageManagementActionList ?? [];

      actionsList.push(action.payload);

      imageManagementTaskReducer.caseReducers.updateImageActionsByTaskID(state, {
        type: 'updateImageActionsByTaskID',
        payload: { taskId: state.selectedImageTaskId, actions: actionsList },
      });
    },
    onDeleteTaskAction: (state, action: PayloadAction<number>) => {
      const actionsList = state.imageManagementTaskList
        ?.find((task) => task.id === state.selectedImageTaskId)
        ?.imageManagementActionList.filter((act) => act.id !== action.payload);

      state.deletedActionIds.push(action.payload);

      imageManagementTaskReducer.caseReducers.updateImageActionsByTaskID(state, {
        type: 'updateImageActionsByTaskID',
        payload: { taskId: state.selectedImageTaskId, actions: actionsList },
      });
    },
    setDeletedActionIds: (state, action) => {
      state.deletedActionIds = action.payload;
    },
    onAddNewImageTask: (state, action: PayloadAction<ImageManagementTaskDTO>) => {
      state.imageManagementTaskList.push(action.payload);
      state.imageManagementTaskOriginalList.push(action.payload);
    },
    onDeleteImageTask: (state, action: PayloadAction<number>) => {
      state.imageManagementTaskList = state.imageManagementTaskList.filter(
        (task) => task.id !== action.payload,
      );
      state.imageManagementTaskOriginalList =
        state.imageManagementTaskOriginalList.filter(
          (task) => task.id !== action.payload,
        );
    },
    updateSelectedImageTask: (state, action: PayloadAction<{ key: string; value }>) => {
      const task = state.imageManagementTaskList.find(
        (t) => t?.id === state.selectedImageTaskId,
      );
      if (task) {
        task[action.payload.key] = action.payload.value;

        if (action.payload.key === imageTaskNameKey) {
          task.isNameUpdated = true;
        } else {
          task.isDirty = true;
        }
      }
      state.isDirty = true;
    },
    updateImageActionConstraints: (state, action) => {
      const actionsList =
        state.imageManagementTaskList?.find(
          (task) => task.id === state.selectedImageTaskId,
        )?.imageManagementActionList || [];
      for (const act of actionsList) {
        if (act?.id === state.selectedImageActionId) {
          act.constraints = action.payload;
          act.isConstraintDirty = true;
        }
      }
      imageManagementTaskReducer.caseReducers.updateImageActionsByTaskID(state, {
        type: 'updateImageActionsByTaskID',
        payload: { taskId: state.selectedImageTaskId, actions: actionsList },
      });
    },
    updateConstraintRules: (state, action: PayloadAction<{ field; value }>) => {
      const actionsList =
        state.imageManagementTaskList?.find(
          (task) => task.id === state.selectedImageTaskId,
        )?.imageManagementActionList || [];
      for (const act of actionsList) {
        if (act?.id === state.selectedImageActionId) {
          const { constraints } = act;
          const index = constraints?.rules?.findIndex(
            (con) => con.field === action.payload.field.id,
          );

          if (index >= 0) {
            const rule = {
              ...constraints.rules[index],
              value: action.payload.value,
              operator: action.payload.value === '' ? 'IS NULL' : '=',
            };
            act.constraints = {
              ...constraints,
              rules: [...constraints.rules],
            };
            act.constraints.rules[index] = rule;
          } else {
            const rule = new SearchConstraintDTO({
              field: action.payload.field.id,
              value: action.payload.value,
              operator: action.payload.value === '' ? 'IS NULL' : '=',
              isDropdown: false,
            });

            act.constraints = {
              ...constraints,
              rules: [...constraints.rules, rule],
            };
          }
          act.isConstraintDirty = true;
        }
      }
      imageManagementTaskReducer.caseReducers.updateImageActionsByTaskID(state, {
        type: 'updateImageActionsByTaskID',
        payload: { taskId: state.selectedImageTaskId, actions: actionsList },
      });
    },
    deleteConstraintRuleByFieldId: (state, action) => {
      const actionsList =
        state.imageManagementTaskList?.find(
          (task) => task.id === state.selectedImageTaskId,
        )?.imageManagementActionList || [];
      for (let i = 0; i < actionsList.length; i++) {
        if (actionsList[i].id === state.selectedImageActionId) {
          const newRules = actionsList[i].constraints?.rules?.filter(
            (rule) => rule.field !== action.payload.id,
          );
          actionsList[i] = {
            ...actionsList[i],
            constraints: { ...actionsList[i].constraints, rules: newRules },
          };
          actionsList[i].isConstraintDirty = true;
        }
      }
      imageManagementTaskReducer.caseReducers.updateImageActionsByTaskID(state, {
        type: 'updateImageActionsByTaskID',
        payload: { taskId: state.selectedImageTaskId, actions: actionsList },
      });
    },
    setResultCount: (state, action) => {
      state.resultCount = action.payload;
    },
    setIsTaskRunning: (state, action: PayloadAction<boolean>) => {
      state.isRunning = action.payload;
    },
    setIsPreview: (state, action: PayloadAction<boolean>) => {
      state.isPreview = action.payload;
    },
    setIsError: (state, action: PayloadAction<boolean>) => {
      state.isError = action.payload;
    },
    setDisplayResults: (state, action: PayloadAction<IImageManagementResultDTO[]>) => {
      state.displayResults = action.payload;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    setClearResults: (state) => {
      state.displayResults = [];
      state.imageManagementTaskResultList = [];
      state.progress = 0;
    },
    batchUpdateImageTaskResultList: (
      state,
      action: PayloadAction<{ key: string; value }>,
    ) => {
      const { key, value } = action.payload;
      state.imageManagementTaskResultList = state.imageManagementTaskResultList.map(
        (result) => {
          return { ...result, [key]: value };
        },
      );
    },
  },
});

export const {
  setImageManagementTaskList,
  setImageManagementTaskOriginalList,
  setImageManagementTasksIsDirty,
  setSelectedImageTaskId,
  setSelectedImageActionId,
  updateImageActionsByTaskID,
  setCheckedExplorerNodes,
  updateSelectedImageTaskActions,
  onAddNewTaskAction,
  onDeleteTaskAction,
  setDeletedActionIds,
  onAddNewImageTask,
  onDeleteImageTask,
  updateSelectedImageTask,
  updateImageActionConstraints,
  updateConstraintRules,
  deleteConstraintRuleByFieldId,
  setImageManagementTaskResultList,
  updateIsImageManagementTaskResultEmpty,
  updateSelectedImageTaskResults,
  updateSelectedImageTaskResultsBatch,
  setIsProcessedChecked,
  setIsPendingChecked,
  setIsErrorChecked,
  setResultCount,
  setIsTaskRunning,
  setIsPreview,
  setIsError,
  setDisplayResults,
  setProgress,
  setClearResults,
  batchUpdateImageTaskResultList,
} = imageManagementTaskReducer.actions;

export default imageManagementTaskReducer.reducer;
